import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useRouter } from 'next/router';
import {
  hasStaleChoice,
  readConsent,
  readGpc,
  readRegion,
  writeConsent,
} from './consentState';
import {
  buildConsent,
  CONSENT_EVENT,
  isAuthPath,
  noticeDefaults,
  REGION_NOTICE,
} from './consentConfig';
import { recordConsent } from './consentLedger';

/**
 * First-party consent state for the dashboard (GDPR plan P1-2/P1-3). Holds the
 * stored category decisions, drives the banner/preference-center visibility,
 * and is the single source of truth every tracker subscribes to.
 *
 * Two rules, picked per visitor by `readRegion()` (see consentConfig.js):
 * - Opt-in (EU/EEA/UK/CH, or region unknown): fail-closed. Until the user
 *   chooses, `categories` is all-off except `necessary` and the banner shows.
 * - Notice (everyone else): defaults are recorded on the first page view
 *   (everything on, or analytics + marketing off under Global Privacy
 *   Control) and a small notice links to the preference center.
 * Sign-in and sign-up pages get neither: nothing is asked or defaulted there.
 */
const ConsentContext = createContext(null);

// Safe defaults when the provider is absent (isolated tests, storybook, etc.).
const FALLBACK = {
  ready: false,
  hasConsent: false,
  categories: buildConsent(false),
  has: (key) => key === 'necessary',
  bannerOpen: false,
  noticeOpen: false,
  noticeGpc: false,
  prefsOpen: false,
  acceptAll() {},
  rejectAll() {},
  save() {},
  openPreferences() {},
  closePreferences() {},
  dismissNotice() {},
};

export function ConsentProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [record, setRecord] = useState(null);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [notice, setNotice] = useState(null); // null | { gpc: bool }
  const [prefsOpen, setPrefsOpen] = useState(false);

  const router = useRouter();
  const onAuthPage = isAuthPath(router?.asPath);

  // Hydrate from the cookie after mount (SSR-safe: server renders nothing, so
  // the first client render matches and there's no hydration mismatch).
  // Notice-region defaults only stand while the visitor is still placed in a
  // notice region; met from an opt-in region they are dropped (fail closed)
  // and the banner asks.
  useEffect(() => {
    const existing = readConsent();
    const defaultsOutOfRegion =
      existing?.isDefault && readRegion() !== REGION_NOTICE;
    setRecord(defaultsOutOfRegion ? null : existing);
    setReady(true);
  }, []);

  const apply = useCallback((categories, source, { isDefault = false } = {}) => {
    const normalized = {
      ...buildConsent(false),
      ...categories,
      necessary: true,
    };
    const written = writeConsent(normalized, { isDefault });
    const next = {
      categories: normalized,
      version: written.v,
      timestamp: written.t,
      isDefault,
    };
    setRecord(next);
    setBannerOpen(false);
    setNotice(null);
    setPrefsOpen(false);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(CONSENT_EVENT, { detail: normalized })
      );
    }
    recordConsent(next, source);
  }, []);

  // No stored choice yet: ask (opt-in region) or record the defaults and show
  // the notice (notice region). Waits while on a sign-in/sign-up page and runs
  // on the first page after it.
  useEffect(() => {
    if (!ready || record || onAuthPage) return;
    if (readRegion() === REGION_NOTICE && !hasStaleChoice()) {
      const gpc = readGpc();
      apply(
        noticeDefaults({ gpc }),
        gpc ? 'notice_default_gpc' : 'notice_default',
        { isDefault: true }
      );
      setNotice({ gpc });
    } else {
      setBannerOpen(true);
    }
  }, [ready, record, onAuthPage, apply]);

  const acceptAll = useCallback(
    () => apply(buildConsent(true), 'accept_all'),
    [apply]
  );
  const rejectAll = useCallback(
    () => apply(buildConsent(false), 'reject_all'),
    [apply]
  );
  const save = useCallback(
    (categories) => apply(categories, 'save_preferences'),
    [apply]
  );
  const openPreferences = useCallback(() => {
    setNotice(null);
    setPrefsOpen(true);
  }, []);
  const dismissNotice = useCallback(() => setNotice(null), []);
  const closePreferences = useCallback(() => setPrefsOpen(false), []);

  const has = useCallback(
    (key) => key === 'necessary' || !!record?.categories?.[key],
    [record]
  );

  const value = {
    ready,
    hasConsent: !!record,
    categories: record?.categories || buildConsent(false),
    has,
    bannerOpen: bannerOpen && !onAuthPage,
    noticeOpen: !!notice && !onAuthPage,
    noticeGpc: !!notice?.gpc,
    prefsOpen,
    acceptAll,
    rejectAll,
    save,
    openPreferences,
    closePreferences,
    dismissNotice,
  };

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent() {
  return useContext(ConsentContext) || FALLBACK;
}
